import Case from 'case';
import React, { useCallback, useEffect, useState } from 'react';
import { View, StyleSheet, FlatList, Pressable } from 'react-native';

import { FlexModal } from '@/components/flex-modal';
import { Text, useTheme, ActivityIndicator, Chip, IconButton } from '@/components/ui';
import { dataService } from '@/services/data-service';
import type { HistoryObjectType, HistoryRecord } from '@/types/history';

const CHANGE_TYPE_COLORS: Record<string, string> = {
	create: '#4CAF50',
	update: '#2196F3',
	delete: '#F44336',
};

const formatValue = (v: unknown): string => {
	if (v === null || v === undefined) return '(empty)';
	if (typeof v === 'string') return v;
	return JSON.stringify(v);
};

const formatTimeAgo = (ts: number): string => {
	const diff = Date.now() - ts;
	const minutes = Math.floor(diff / 60000);
	if (minutes < 1) return 'just now';
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	if (days < 30) return `${days}d ago`;
	return new Date(ts).toLocaleDateString();
};

interface HistoryModalProps {
	visible: boolean;
	onDismiss: () => void;
	objectType: HistoryObjectType;
	objectId: string;
	title?: string;
	/** Content rendered above the entries, in every state (loading, empty, list). */
	pinned?: React.ReactNode;
}

export const HistoryModal: React.FC<HistoryModalProps> = ({
	visible,
	onDismiss,
	objectType,
	objectId,
	title = 'History',
	pinned,
}) => {
	const theme = useTheme();
	const [records, setRecords] = useState<HistoryRecord[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
	const [users, setUsers] = useState<Record<string, string>>({});

	const fetchHistory = useCallback(async (signal?: AbortSignal) => {
		setLoading(true);
		setError(null);
		try {
			const [result, usersResult] = await Promise.all([
				dataService.getHistory({
					objectType,
					objectId,
					page,
					limit: 50,
				}),
				Object.keys(users).length === 0
					? dataService.getUsers({ limit: 500, includeArchived: true })
					: Promise.resolve(null),
			]);
			if (signal?.aborted) return;
			setRecords(result.data);
			setTotalPages(result.pagination.pages);
			if (usersResult) {
				const userMap: Record<string, string> = {};
				for (const u of usersResult.data) {
					userMap[u.id] = u.name;
				}
				setUsers(userMap);
			}
		} catch (err) {
			if (signal?.aborted) return;
			console.error('Failed to load history:', err);
			setError('Failed to load history.');
		}
		setLoading(false);
	}, [objectType, objectId, page, users]);

	useEffect(() => {
		if (!visible) return;
		const controller = new AbortController();
		fetchHistory(controller.signal);
		return () => controller.abort();
	}, [visible, fetchHistory]);

	// Reset state when modal opens with new object
	useEffect(() => {
		if (visible) {
			setPage(1);
			setExpandedIds(new Set());
		}
	}, [visible, objectId]);

	const toggleExpand = useCallback((id: string) => {
		setExpandedIds(prev => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);

	const renderRecord = useCallback(({ item }: { item: HistoryRecord }) => {
		const isExpanded = expandedIds.has(item.id);
		const hasValues = item.oldValue !== undefined || item.newValue !== undefined;

		const summary = item.changeType === 'update' && item.property
			? `${Case.title(item.property.replace(/_/g, ' '))}`
			: item.changeType === 'create'
				? `Created ${Case.title(objectType.replace(/_/g, ' ').replace(/s$/, ''))}`
				: item.changeType === 'delete'
					? `Deleted ${Case.title(objectType.replace(/_/g, ' ').replace(/s$/, ''))}`
					: item.property || '';

		return (
			<Pressable
				onPress={() => hasValues && toggleExpand(item.id)}
				style={[
					localStyles.record,
					{ borderBottomColor: theme.colors.surfaceVariant },
				]}
			>
				<View style={localStyles.recordHeader}>
					<Chip
						mode="flat"
						compact
						textStyle={{ color: '#fff', fontSize: 11 }}
						style={{ backgroundColor: CHANGE_TYPE_COLORS[item.changeType] || '#999' }}
					>
						<Text style={{ color: '#fff', fontSize: 11 }}>{item.changeType}</Text>
					</Chip>
					<Text style={[localStyles.summary, { color: theme.colors.onSurface }]} numberOfLines={isExpanded ? undefined : 1}>
						{hasValues && (isExpanded ? '\u25BC ' : '\u25B6 ')}{summary}
					</Text>
				</View>
				<View style={localStyles.recordMeta}>
					<Text style={[localStyles.metaText, { color: theme.colors.onSurfaceVariant }]}>
						{users[item.userId || ''] || item.userId?.slice(0, 8) || 'System'}
					</Text>
					<Text style={[localStyles.metaText, { color: theme.colors.onSurfaceVariant }]}>
						{formatTimeAgo(item.ts)}
					</Text>
				</View>
				{isExpanded && (
					<View style={[localStyles.expandedContent, { backgroundColor: theme.colors.surfaceVariant + '44' }]}>
						{item.oldValue !== undefined && (
							<View style={localStyles.valueBlock}>
								<Text style={[localStyles.valueLabel, { color: theme.colors.error }]}>Old:</Text>
								<Text style={[localStyles.valueText, { color: theme.colors.onSurface }]} selectable>
									{formatValue(item.oldValue)}
								</Text>
							</View>
						)}
						{item.newValue !== undefined && (
							<View style={localStyles.valueBlock}>
								<Text style={[localStyles.valueLabel, { color: theme.colors.primary }]}>New:</Text>
								<Text style={[localStyles.valueText, { color: theme.colors.onSurface }]} selectable>
									{formatValue(item.newValue)}
								</Text>
							</View>
						)}
						<Text style={[localStyles.metaText, { color: theme.colors.onSurfaceVariant, marginTop: 4 }]}>
							{new Date(item.ts).toLocaleString()}
						</Text>
					</View>
				)}
			</Pressable>
		);
	}, [expandedIds, users, theme, objectType, toggleExpand]);

	return (
		<FlexModal visible={visible} onDismiss={onDismiss}>
			<View style={localStyles.header}>
				<Text style={[localStyles.title, { color: theme.colors.onSurface }]}>{title}</Text>
				<IconButton icon="close" size={24} onPress={onDismiss} />
			</View>

			{pinned ? <View testID="history-pinned" style={localStyles.pinned}>{pinned}</View> : null}

			{loading && records.length === 0 ? (
				<ActivityIndicator size="large" style={{ marginTop: 40 }} />
			) : error ? (
				<View style={localStyles.errorContainer}>
					<Text style={{ color: theme.colors.error }}>{error}</Text>
				</View>
			) : records.length === 0 ? (
				<View style={localStyles.emptyContainer}>
					<Text style={{ color: theme.colors.onSurfaceVariant }}>No history records found.</Text>
				</View>
			) : (
				<>
					<FlatList
						data={records}
						keyExtractor={(item) => item.id}
						renderItem={renderRecord}
						style={{ flex: 1 }}
					/>
					{totalPages > 1 && (
						<View style={localStyles.pagination}>
							<Chip
								mode="outlined"
								onPress={() => setPage(p => Math.max(1, p - 1))}
								disabled={page <= 1}
							>
								<Text>Previous</Text>
							</Chip>
							<Text style={{ color: theme.colors.onSurfaceVariant }}>
								Page {page} of {totalPages}
							</Text>
							<Chip
								mode="outlined"
								onPress={() => setPage(p => Math.min(totalPages, p + 1))}
								disabled={page >= totalPages}
							>
								<Text>Next</Text>
							</Chip>
						</View>
					)}
				</>
			)}
		</FlexModal>
	);
};

const localStyles = StyleSheet.create({
	header: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 12,
	},
	title: {
		fontSize: 18,
		fontWeight: '700',
	},
	pinned: {
		marginBottom: 12,
		gap: 6,
	},
	record: {
		paddingVertical: 10,
		borderBottomWidth: 1,
	},
	recordHeader: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
	},
	summary: {
		flex: 1,
		fontSize: 14,
	},
	recordMeta: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		marginTop: 4,
	},
	metaText: {
		fontSize: 12,
	},
	expandedContent: {
		marginTop: 8,
		padding: 10,
		borderRadius: 6,
	},
	valueBlock: {
		marginBottom: 6,
	},
	valueLabel: {
		fontSize: 11,
		fontWeight: '600',
		marginBottom: 2,
	},
	valueText: {
		fontSize: 13,
		fontFamily: 'monospace',
	},
	errorContainer: {
		padding: 20,
		alignItems: 'center',
	},
	emptyContainer: {
		padding: 40,
		alignItems: 'center',
	},
	pagination: {
		flexDirection: 'row',
		justifyContent: 'center',
		alignItems: 'center',
		gap: 12,
		paddingTop: 12,
	},
});
